const HONORIFICS = /^(mr|mrs|ms|miss|dr|prof)\.?$/i;

function initialsOf(name) {
  const words = name
    .split(/\s+/)
    .filter(Boolean)
    .filter((word) => !HONORIFICS.test(word));

  return words
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');
}

function PersonGlyph() {
  return (
    <svg viewBox="0 0 24 24" className="size-1/2 text-sage" aria-hidden="true">
      <circle cx="12" cy="8.5" r="4" fill="currentColor" />
      <path d="M3.5 21a8.5 8.5 0 0 1 17 0Z" fill="currentColor" />
    </svg>
  );
}

/**
 * Stands in for the design canvas's <image-slot>. It always fills its wrapper,
 * so the parent keeps the aspect ratio, border and clip-path.
 *
 * Pass `src` once you have a real photo in `public/img/` and it renders an
 * <img>; until then it draws an on-brand placeholder — initials for people,
 * a captioned frame for everything else.
 */
export default function ImageSlot({ src, alt = '', name, caption, shape = 'rect' }) {
  const rounding = shape === 'circle' ? 'rounded-full' : '';

  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className={`size-full object-cover ${rounding}`}
      />
    );
  }

  if (name) {
    return (
      <div
        role="img"
        aria-label={alt || `Portrait of ${name}`}
        className={`flex size-full items-center justify-center bg-forest [container-type:size] ${rounding}`}
      >
        {/* Scales with the slot: the same component serves 38px avatars and
            full-width teacher portraits. */}
        <span className="select-none font-display font-extrabold text-cream text-[clamp(13px,38cqmin,52px)]">
          {initialsOf(name)}
        </span>
      </div>
    );
  }

  if (shape === 'circle') {
    return (
      <div
        role="img"
        aria-label={alt || 'Photo placeholder'}
        className={`flex size-full items-center justify-center bg-mint ${rounding}`}
      >
        <PersonGlyph />
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={alt || caption || 'Photo placeholder'}
      className="flex size-full items-center justify-center bg-mint p-4"
    >
      <span className="max-w-[24ch] text-center font-hand text-lg leading-tight text-forest/55">
        {caption || 'Photo'}
      </span>
    </div>
  );
}
